/*
 * ONE RUN'S TRANSCRIPT, addressed by run id.
 *
 * WHY RUN ID AND NOT CONVERSATION ID, which is what the archive is keyed by. Every surface this
 * track is building — the transcript detail view, a sub-agent's thread, a node opened out of the
 * hierarchy — is looking at a RUN. The conversation is an implementation detail of where that run's
 * words are stored (`AgentRun.conversation_id`: "open it and you are talking to the agent"), and a
 * reader that had to carry both ids would be carrying one it cannot obtain on its own. A run id is a
 * complete address; a link to a nested agent is one string.
 *
 * That is also why the tree read resolves the root server-side. `TreeView` is keyed by root, so
 * asking it about a leaf used to match nothing — the reader would need the root it does not have.
 *
 * Manager-pattern, like `RunTree`: it does the IO, holds the result, and emits `change`. The view
 * subscribes and reads, and never fetches (the Cardinal Rule). It does NOT poll — a transcript is
 * the record of what was said, and the live view of a run in flight is the chat surface, which has
 * its own stream. Re-reading is explicit: `open` again, or `more` for the previous page.
 */
import { create, toBinary, fromBinary } from '@bufbuild/protobuf';
import { TreeViewRequestSchema, TreeViewReplySchema } from '@vitesse/proto/vitesse/ai/v1/agent_pb.js';
import { treeHost } from './driver';
import { foldTranscript } from './transcript-fold';
/** The read this needs from the AI api, and nothing more — so a test drives it with a function. */
/**
 * THE ATTEMPT A THREAD SHOWS: the newest in its conversation — not the one it was opened on.
 *
 * An agent that is woken or continued becomes a NEW run in the SAME conversation. The thread bound
 * the run id it was opened with, so after a wake it still showed the finished attempt: no Stop, a
 * poll that had already stopped, and the new attempt's reply never painted. Observed live on
 * 2026-10-29 — the reply appeared only after reopening the thread from the tree.
 *
 * The newest is also the live one whenever there is one: the store continues only a run that has
 * returned, so a conversation never has two live attempts and the live one is always the last to
 * start. That makes this the server's rule for which run a message in the thread steers
 * (`liveRunByConversation`) and the tree's rule for a node (`latestAttempt`) at once.
 */
export function currentAttempt(runs, runId) {
    const opened = runs.find((r) => r.runId === runId);
    if (!opened?.conversationId)
        return opened;
    return runs.filter((r) => r.conversationId === opened.conversationId).reduce((a, b) => (b.startedAtNs > a.startedAtNs ? b : a));
}
export class RunTranscript extends EventTarget {
    constructor(history) {
        super();
        this.history = history;
        this._runId = '';
        this._turns = [];
        this._cursor = '';
        this._loading = false;
        this._error = '';
    }
    /** Which run is on screen. Empty before the first `open`. */
    get runId() {
        return this._runId;
    }
    /** The run itself — its label, objective, status, what it returned. A view renders the header from
     *  this rather than being handed a second copy by whoever opened it. */
    get run() {
        return this._run;
    }
    /** Oldest first, as they were said. */
    get turns() {
        return this._turns;
    }
    /** True only while the FIRST page is in flight. Loading an older page must not blank a transcript
     *  the reader is already looking at. */
    get loading() {
        return this._loading;
    }
    /** Prose, never a code — the platform rule. Empty when the last read succeeded. */
    get error() {
        return this._error;
    }
    /** Is there an older page to load? */
    get hasMore() {
        return this._cursor !== '';
    }
    /**
     * Read one run's transcript.
     *
     * Re-opening the SAME run re-reads deliberately: a run that has since taken more steps has more to
     * show, and the alternative — a cache keyed by run id — would make an agent look finished at
     * whatever moment it was first opened.
     */
    async open(runId) {
        this._runId = runId;
        this._run = undefined;
        this._turns = [];
        this._cursor = '';
        this._error = '';
        if (!runId)
            return this.emit();
        this._loading = true;
        this.emit();
        try {
            this._run = await this.lookup(runId);
            if (!this._run) {
                /*
                 * A run id that names nothing is a stale link, not a failure of the read — said plainly,
                 * because the reader's next move is to go back rather than to retry.
                 *
                 * ONLY when the read itself said nothing, which is the distinction this used to lose. A
                 * refusal also produces no run, and overwriting it here told someone who lacked access that
                 * their link was stale: the wrong next move, and a permission problem nobody would report.
                 */
                this._error ||= 'That agent run no longer exists.';
                return;
            }
            await this.page('');
        }
        catch (e) {
            this._error = e instanceof Error ? e.message : String(e);
        }
        finally {
            this._loading = false;
            this.emit();
        }
    }
    /**
     * Re-read the RUN, and only the run.
     *
     * The turns have their own path — a watched thread rehydrates from the archive on every audit —
     * but the run itself came from one lookup when the view opened and then never moved. So an agent
     * that finished while you were watching it kept a running clock and a live status until you left
     * the page and came back: the same snapshot bug the transcript had, one level up.
     *
     * The lookup alone, deliberately. `open` would re-read the whole transcript that the thread's own
     * refresh has already read, on every tick.
     */
    async refreshRun() {
        if (!this._runId)
            return;
        const runId = this._runId;
        const run = await this.lookup(runId).catch(() => undefined);
        // The view may have moved on while the read was in flight; writing then would show one agent's
        // status over another's thread.
        if (!run || this._runId !== runId)
            return;
        this._run = run;
        this.emit();
    }
    /** Load the page before the oldest one held. No-op when there is nothing older or a read is in
     *  flight — a reader who scrolls hard should not queue five identical requests. */
    async more() {
        if (!this._cursor || !this._run)
            return;
        const cursor = this._cursor;
        this._cursor = '';
        try {
            await this.page(cursor);
        }
        catch (e) {
            this._error = e instanceof Error ? e.message : String(e);
            this._cursor = cursor; // the page was not read; the reader may try again
        }
        this.emit();
    }
    /** Find the run in its own tree — and, if it has been woken since, the attempt that is the agent
     *  now (`currentAttempt`). The server resolves the root, so a leaf id is a complete address. */
    async lookup(runId) {
        const bytes = await treeHost().treeView(toBinary(TreeViewRequestSchema, create(TreeViewRequestSchema, { rootRunId: runId, wantRuns: true })));
        const reply = fromBinary(TreeViewReplySchema, bytes);
        if (reply.result?.error) {
            this._error = reply.result.summary || 'that run could not be read';
            return undefined;
        }
        return currentAttempt(reply.runs, runId);
    }
    async page(cursor) {
        const conversationId = this._run?.conversationId ?? '';
        // A run with no conversation has not said anything yet — queued, or spawned and not started. An
        // empty transcript is the truthful rendering; a refusal would read as breakage.
        if (!conversationId)
            return;
        const page = await this.history(conversationId, cursor);
        const older = foldTranscript(page.messages);
        // PREPENDED, because paging walks backwards through the archive while the reader reads forwards.
        this._turns = cursor ? [...older, ...this._turns] : older;
        this._cursor = page.nextCursor;
    }
    emit() {
        this.dispatchEvent(new CustomEvent('change'));
    }
}
//# sourceMappingURL=run-transcript.js.map